<template>

    <div
      id="app"
      v-bind:class="[ $route.meta.screensaver ? 'app_screensaver' : '' ]"
      @keydown="onKeyDown"
      @mousemove="onMouseMove"
      @scroll="onScroll"
      v-on:click="onClick">
      <transition name="fade-app">
        <div id="app-holder" v-show="showApp">
        <!-- Header -->
        <!-- add to hide top menu on screensaver v-if="!$route.meta.screensaver" -->
        <div class="app_header_bg" v-if="!$route.meta.screensaver">
          <header class="app_header">
            <div
              class="header__logo"
              @click="onLogoClick">
              <img
                src="./assets/theme/images/header-logo.png"
                :alt="$t('message.headerLogoAlt')">
            </div>
            <!-- app's chat widget  -->
            <app-chat-widget :chatWidgetScriptUrl='chatWidgetScriptUrl' :title="$t('message.chatTitle')"
            :subtitle="$t('message.chatSubTitle')"
            v-if="!isKiosk"  />
            <!-- Button -->
            <!-- for now logo click  is fine as it clears state too, and on screensaver logic will be different probably  -->
            <AppButton
              v-if="isKiosk && !this.$route.meta.screensaver"
              @click.native="onLogoClick"
              class="back_to_start_button"
              :state="backButtonState"
              :type="backButtonType"
              :text="$t('message.backToStart')"
              :hasIconLeft="true" />
          </header>
        </div>

        <!-- Title section -->
        <div class="app_title_bg" v-if="!$route.meta.screensaver">
          <div class="container">
            <transition
              name="view-fade"
              mode="out-in">
              <!-- Hack :) Stops any height jumps when title fades in. It achieves this as the transition
              fades our empty div *out* while the page is leaving, and our title view *in* while the page is entering
              so the height change magically occurs when *no* page is in view. Alternatives would be to delay animation
              by $default-transition-duration on the *in* phase but then we'd need another transition class and that was
              more code (although did work). I liked the hack for robustness and never having to care if we are entering, or
              leaving, or changing text. It should just work -->
              <div
                class="app__empty"
                v-if="!appTitle"></div>
              <h1
                class="app__title_section"
                v-if="appTitle"
                :key="appTitle">
                {{ $t(appTitle) }}
              </h1>
            </transition>
          </div>

          <!-- progress bar -->
          <transition
            name="view-fade"
            mode="out-in">
            <!-- Hack - see notes above -->
            <div
              class="app__empty"
              v-if="!currentStep"></div>
            <progress-bar
              v-if="currentStep"
              :currentStep="currentStep"
              :totalSteps="totalSteps" />
          </transition>
        </div>

        <!-- Main view -->
        <main>
          <div v-bind:class="[ $route.meta.screensaver ? 'app_main_container screensaver' : 'app_main_container' ]">
          <!-- @todo need to make this more dynamic and not attached to every page -->
          <transition
            name="view-fade"
            mode="out-in"
            @before-leave="onBeforeViewLeave"
            @after-enter="onAfterViewEnter">
            <router-view></router-view>
          </transition>
          </div>
        </main>

        <!-- Footer -->
        <footer class="app_footer" v-if="!$route.meta.screensaver">
          <div class="container">
            <span class="footer__text">&copy; {{ footerUrl }} {{ versionString }}</span>
          </div>
        </footer>
        </div>
      </transition>
    </div>

</template>

<script>
  import api from '@/api/api'
  import cloud from '@/cloud/cloud'
  import {ButtonStates, ButtonTypes} from '@/components/app-button'
  import appSettings from '@/app-settings.json'
  import themeSettings from '@/theme-settings.json'
  import progressBar from '@/components/progress-bar'
  import appChatWidget from '@/components/app-chat-widget'
  import { Validator } from 'vee-validate'
  import { loadLanguageAsync } from '@/lang'

  export default {
    name: 'app',
    components: {
      progressBar,
      appChatWidget
    },
    data: function () {
      // we pass the appTitle and currentStep meta to the data object to
      // be able to access for the initial route
      return {
        backButtonType: ButtonTypes.SENARY,
        backButtonState: ButtonStates.ENABLED,
        showApp: true,
        appTitle: this.$route.meta.appTitle,
        screensaver: this.$route.meta.screensaver,
        currentStep: this.$route.meta.currentStep,
        isKiosk: !!appSettings.kioskSettings,
        kioskSettings: appSettings.kioskSettings,
        mailTo: themeSettings.theme.mailTo,
        footerUrl: themeSettings.theme.footerUrl,
        versionString: appSettings.versionString,
        languages: [],
        languageIndex: 0
      }
    },
    computed: {
      totalSteps: {
        get () {
          return appSettings.progressBar.totalSteps
        }
      },
      chatWidgetScriptUrl: {
        get () {
          return themeSettings.chatWidgetScriptUrl
        }
      }
    },
    mounted () {
      if (this.isKiosk) {
        this.screensaverTimeoutReset()
        this.fadeInit()
        // The wake lock sentinel.
        this.wakeLock = null
        this.requestWakeLock()

        // if in kiosk mode we rotate the langauages as we fade in/out so
        // we need to load them
        api.getLanguages()
          .then(response => {
            this.languages = response.data
          })
          .catch(error => {
            console.log(error)
          })
        // as long as we're not on screensaver already, go there
        if (!this.$route.meta.screensaver) {
          this.$router.push('/screensaver')
          this.fadeInit()
        }
      }

      // scale the elements as needed
      if (appSettings.zoomAppHeader) {
        console.log('set header zoom')
        document.getElementsByClassName('app_header_bg')[0].style.zoom = appSettings.zoomAppHeader
      }
      if (appSettings.zoomAppTitle) {
        document.getElementsByClassName('app_title_bg')[0].style.zoom = appSettings.zoomAppTitle
      }
      if (appSettings.zoomAppMain) {
        document.getElementsByClassName('app_main_container')[0].style.zoom = appSettings.zoomAppMain
      }
      if (appSettings.zoomAppFooter) {
        document.getElementsByClassName('app_footer')[0].style.zoom = appSettings.zoomAppFooter
      }
    },
    methods: {
      /**
       * Once transition to new view has started change appTitle
       * and current step to new values
       * Scrolls to top before each transition
       */
      onBeforeViewLeave: function () {
        this.appTitle = this.$route.meta.appTitle
        this.currentStep = this.$route.meta.currentStep
        window.scrollTo(0, 0)
      },

      /**
       * JS hook for transition to new page has finished
       */
      onAfterViewEnter: function () {
        // ensure we reset screensaver as certain pages (e.g. success) will
        // require an upated duration
        console.log('after enter')
        this.screensaverTimeoutReset()
      },
      /**
       * Send back to homepage
       */
      // @todo change logic to clear history
      onLogoClick: function () {
        // on kiosk our hp is screensaver!
        if (this.isKiosk) {
          this.$router.push('/screensaver')
        } else {
          this.$router.push('/')
        }
      },

      /**
       * Method executed when someone
       * clicked on a key e.g. on a keyboard
       */
      onKeyDown: function () {
        this.screensaverTimeoutReset()
      },

      /**
       * Method executed when someone moves mouse on app
       */
      onMouseMove: function () {
        this.screensaverTimeoutReset()
      },

      /**
       * Method executed when someone scrolls
       */
      onScroll: function () {
        this.screensaverTimeoutReset()
      },

      /**
       * Method executed when someone clicks on soemthing
       */
      onClick: function () {
        // if we on the screensaver then stop fading for a length of time
        if (this.isKiosk && this.$route.meta.screensaver) {
          this.fadeReset()
        } else if (this.isKiosk) {
          this.screensaverTimeoutReset()
        }
      },

      /**
       * Method clears existing timeout
       * and creates new one
       * Not working if not kiosk or we are in screensaver already
       */
      screensaverTimeoutReset: function () {
        // not kiosk app or screensaver app? ignore activity
        if (!this.isKiosk) {
          return // exit
        }
        // if we're no longer on screensaver then app should now be visible
        this.showApp = true
        // clear existing timeout
        if (this.screensaverTimeoutId) {
          clearTimeout(this.screensaverTimeoutId)
        }
        // screensaver timeout - how much time of inactivity to open screensaver
        var timeoutValue = this.kioskSettings.screensaverTimeout ? this.kioskSettings.screensaverTimeout : 60000
        // success page? use different setup
        if (this.$route.meta.successPage) {
          timeoutValue = this.kioskSettings.successPageScreensaverTimeout
            ? this.kioskSettings.successPageScreensaverTimeout : 10000
        }

        this.screensaverTimeoutId = setTimeout(() => {
          // as long as we're not on screensaver already, go there
          if (this.isKiosk && !this.$route.meta.screensaver) {
            this.$store.commit('setTimeWhenScreensaver', null)
            this.$router.push('/screensaver')
            this.fadeInit()
          }
        }, timeoutValue)

        // set the time we expect the claim to timeout (screensaver to open)
        var timeWhenScreensaver = new Date()
        timeWhenScreensaver.setMilliseconds(timeWhenScreensaver.getSeconds() + timeoutValue)
        // store timeout
        this.$store.commit('setTimeWhenScreensaver', timeWhenScreensaver)
      },

      /**
       * Function that attempts to request a screen wake lock.
       */
      requestWakeLock: async function () {
        // not kiosk app? ignore request
        if (!this.isKiosk) {
          return // exit
        }
        try {
          this.wakeLock = await navigator.wakeLock.request('screen')
          this.wakeLock.addEventListener('release', () => {
            console.log('Screen Wake Lock released:', this.wakeLock.released)
          })
          console.log('Screen Wake Lock released:', this.wakeLock.released)
        } catch (err) {
          console.error(`${err.name}, ${err.message}`)
        }
      },

      /**
       * Method clears existing fade in/out timer
       */
      fadeClear: function () {
        // clear existing timeout
        if (this.fadeTimeout) {
          clearTimeout(this.fadeTimeout)
          this.fadeTimeout = null
        }
      },

      /**
       * Method resets the fade timer on user touch
       * i.e. stop fading in/out for a period of time
       */
      fadeReset: function () {
        // clear existing timeout
        this.fadeClear()
        // only start fading again after set amount of time
        if (this.fadeResetTimeout) {
          clearTimeout(this.fadeResetTimeout)
        }
        this.fadeResetTimeout = setTimeout(() => {
          if (this.isKiosk && this.$route.meta.screensaver) {
            this.fadeInit()
          }
        }, this.kioskSettings.fadeTimeout ? this.kioskSettings.fadeTimeout : 5000)
        // make sure app is visible
        this.showApp = true
      },

      /**
       * Init the fade in/out timer
       */
      fadeInit: function () {
        this.fadeClear()
        this.fadeTimeout = setTimeout(() => {
          if (this.isKiosk && this.$route.meta.screensaver) {
            this.showApp = !this.showApp
            // switch the language each time we fade back in
            if (this.showApp) this.setNextLanguage()
            this.fadeInit()
          } else {
            // safety play
            this.show = true
          }
        }, this.kioskSettings.fadeDuration ? this.kioskSettings.fadeDuration : 1500)
      },

      /**
       * Helper to move to the next language in a rolling list
       */
      setNextLanguage: function () {
        if (this.languages) {
          this.languageIndex++
          this.languageIndex = this.languageIndex % this.languages.length
          var language = this.languages[this.languageIndex]
          if (language) {
            this.$store.commit('setLanguage', language)
            Validator.localize(language.languageIsoCode)
            loadLanguageAsync(language.languageIsoCode).then(() => {
              console.log(language.languageIsoCode + ' strings loaded')
            })
          }
        }
      }
    },
    beforeCreate: function () {
      // init our cloud modules for the app instance
      cloud.initialize({ firebaseConfig: themeSettings.firebaseConfig })
      // if path has claim-status or screensaver (custom page, ignore redirecting to home page and could init)
      if (window.location.href.indexOf('claim-status') < 0 && window.location.href.indexOf('screensaver') < 0) {
        this.$router.push('/').catch(() => {})
      }

      // load language from url path
      if (this.$route.query.claimLanguageIso) {
        Validator.localize(this.$route.query.claimLanguageIso)
        // load language, nothing else to do
        loadLanguageAsync(this.$route.query.claimLanguageIso).then(() => {})
      }
    }
  }
</script>

<style lang="scss">
  @import "./assets/sass/main.scss";
  .fade-app-enter-active,
  .fade-app-leave-active {
    transition: opacity 1.5s ease;
  }

  .fade-app-enter,
  .fade-app-leave-to {
    opacity: 0;
  }
</style>
